import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { CSS2DRenderer, CSS2DObject } from 'three/addons/renderers/CSS2DRenderer.js';
import { EngineUtils } from './engineUtils.js';
import { initMainScene } from './mainScene.js';
import AssetRepository from './engine-lite/assetRepository.js';
import { MobileControls } from './mobileControls.js';
import { TextureOptimizer } from './textureOptimizer.js';

let camera, scene, renderer, labelRenderer, composer, orbitControls, FridgeDoor, ExitCube;
let isAnimating = false, kitchenSceneActive = true, hoverMarker, worldLabel;
const CAMERA_HEIGHT = 1.5;

export const Loader = {
    show: (text = "Загрузка...") => {
        const el = document.getElementById('loader-overlay');
        const txt = document.getElementById('loader-text');
        if (txt) txt.innerText = text;
        el.style.display = 'flex'; el.style.opacity = '1';
    },
    hide: () => {
        const el = document.getElementById('loader-overlay');
        el.style.opacity = '0';
        setTimeout(() => { el.style.display = 'none'; }, 500);
    }
};

export async function initKitchen() {
    const container = document.getElementById('container');
    const labelContainer = document.getElementById('labels-container') || document.body;

    Loader.show("Загрузка кухни...");
    scene = EngineUtils.createScene(0xffffff);
    renderer = EngineUtils.createRenderer(container);
    EngineUtils.setupLighting(scene, ['p1']);

    labelRenderer = new CSS2DRenderer();
    labelRenderer.setSize(window.innerWidth, window.innerHeight);
    labelRenderer.domElement.style.position = 'absolute';
    labelRenderer.domElement.style.top = '0px';
    labelRenderer.domElement.style.pointerEvents = 'none';
    labelContainer.appendChild(labelRenderer.domElement);

    camera = EngineUtils.createCamera(60, 0.1, 50, 2);
    camera.position.set(9, CAMERA_HEIGHT, 0);

    orbitControls = new OrbitControls(camera, renderer.domElement);
    orbitControls.target.set(8.9, CAMERA_HEIGHT, 0);
    orbitControls.enableDamping = true;
    orbitControls.enableZoom = false;

    const assetRepo = new AssetRepository();
    await assetRepo.loadAssetsFromAssetListFile("/assets/prefabsList.json");
    const modelScene1 = await assetRepo.createFromAsset("sc1");

    modelScene1.traverse((obj) => {
        if (obj.isMesh) {
            obj.userData.interactive = true;
            TextureOptimizer.optimizeMesh(obj);
        }
    });
    scene.add(modelScene1);

    setupObjects(modelScene1);
    const bulb = document.getElementById('hint-bulb');
    const bubble = document.getElementById('hint-text-bubble');
    bulb.style.display = 'flex';
    if (bulb) {
        bulb.onclick = () => {
            bubble.style.display = 'block';
            setTimeout(() => { bubble.style.display = 'none'; }, 3000);
        };
    }
    if (MobileControls.isTouchDevice()) {
        MobileControls.init(() => {
            if (isAnimating) return;
            const dir = new THREE.Vector3();
            camera.getWorldDirection(dir);
            dir.y = 0;
            dir.normalize();

            const targetPoint = camera.position.clone().add(dir.clone().multiplyScalar(0.65));
            processMove(targetPoint, dir);
        });
        MobileControls.show();
        renderer.domElement.addEventListener('click', handleMovement);
    } else {
        renderer.domElement.addEventListener('dblclick', handleMovement);
    }

    composer = EngineUtils.createComposer(renderer, scene, camera);
    renderer.domElement.addEventListener('mousemove', handleHover);
    window.addEventListener('resize', onWindowResize);

    Loader.hide();
    renderer.setAnimationLoop(animateKitchen);
}

function setupObjects(modelScene1) {
    ExitCube = new THREE.Mesh(new THREE.BoxGeometry(0.1, 0.1, 0.1), new THREE.MeshPhongMaterial({ color: "#4376d6" }));
    ExitCube.position.set(6.2, 1.5, -1.2);
    ExitCube.userData = { interactive: true, type: 'exit' };
    scene.add(ExitCube);

    const textDiv = document.createElement('div');
    textDiv.className = 'world-label';
    textDiv.innerText = "У вас пропал обед...";
    worldLabel = new CSS2DObject(textDiv);
    worldLabel.position.set(0, 0.2, 0);
    worldLabel.visible = false; // Изначально скрываем надпись
    ExitCube.add(worldLabel);

    FridgeDoor = modelScene1.getObjectByName("Object001");
    if (FridgeDoor) FridgeDoor.userData.isOpen = false;

    hoverMarker = new THREE.Mesh(new THREE.SphereGeometry(0.05, 8, 8), new THREE.MeshBasicMaterial({ color: "#83ec21", transparent: true, opacity: 0.6 }));
    hoverMarker.visible = false;
    scene.add(hoverMarker);
}

function onWindowResize() {
    EngineUtils.handleResize(camera, renderer, composer);
    labelRenderer.setSize(window.innerWidth, window.innerHeight);
}

function handleHover(event) {
    if (!kitchenSceneActive || isAnimating || MobileControls.isTouchDevice()) return;
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(EngineUtils.getMouseNDC(event, renderer.domElement), camera);
    const intersects = raycaster.intersectObjects(scene.children, true);
    const target = intersects.find(hit => hit.object.userData.interactive);
    if (target) {
        hoverMarker.position.set(target.point.x, 0.1, target.point.z);
        hoverMarker.visible = true;
        document.body.style.cursor = 'pointer';
    } else {
        hoverMarker.visible = false;
        document.body.style.cursor = 'default';
    }
}

async function handleMovement(event) {
    if (isAnimating || !kitchenSceneActive) return;
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(EngineUtils.getMouseNDC(event, renderer.domElement), camera);
    const intersects = raycaster.intersectObjects(scene.children, true);
    const target = intersects.find(hit => hit.object.userData.interactive);

    if (target) {
        if (target.object === ExitCube) {
            worldLabel.visible = true; // Показываем надпись при клике
            isAnimating = true; // Блокируем другие действия
            await new Promise(r => setTimeout(r, 2000)); // Ждем 2 секунды, чтобы игрок успел прочитать
            switchToMainScene();
            return;
        }

        const moveDir = new THREE.Vector3().subVectors(target.point, camera.position);
        moveDir.y = 0;

        let isFridge = (target.object === FridgeDoor);
        target.object.traverseAncestors(a => { if (a === FridgeDoor) isFridge = true; });

        if (isFridge && moveDir.length() <= 1.2) { animateFridgeDoor(); return; }

        if (!MobileControls.isTouchDevice()) {
            processMove(target.point, moveDir.normalize());
        }
    }
}

function processMove(targetPoint, moveDir) {
    const targetPos = new THREE.Vector3().copy(targetPoint);
    if (!MobileControls.isTouchDevice()) {
        targetPos.sub(moveDir.clone().multiplyScalar(0.3));
    }
    targetPos.y = CAMERA_HEIGHT;

    const lookAtPoint = targetPos.clone().add(moveDir.clone().multiplyScalar(0.1));
    animateCamera(targetPos, lookAtPoint);
}

function animateCamera(targetPos, lookTarget) {
    isAnimating = true;
    const startPos = camera.position.clone();
    const startOrbit = orbitControls.target.clone();
    const startTime = Date.now();

    const step = () => {
        const progress = Math.min((Date.now() - startTime) / 800, 1);
        const ease = progress * (2 - progress);
        camera.position.lerpVectors(startPos, targetPos, ease);
        orbitControls.target.lerpVectors(startOrbit, lookTarget, ease);
        camera.lookAt(orbitControls.target);
        if (progress < 1) requestAnimationFrame(step);
        else { isAnimating = false; orbitControls.update(); }
    };
    step();
}

function animateFridgeDoor() {
    isAnimating = true;
    const bulb = document.getElementById('hint-bulb');
    if (bulb) bulb.style.display = 'none';
    const startRot = FridgeDoor.rotation.y, endRot = FridgeDoor.userData.isOpen ? 0 : -Math.PI / 2, startTime = Date.now();
    const step = () => {
        const progress = Math.min((Date.now() - startTime) / 600, 1);
        FridgeDoor.rotation.y = startRot + (endRot - startRot) * (progress * (2 - progress));
        if (progress < 1) requestAnimationFrame(step);
        else { FridgeDoor.userData.isOpen = !FridgeDoor.userData.isOpen; isAnimating = false; }
    };
    step();
}

async function switchToMainScene() {
    isAnimating = true;
    const subtitle = document.getElementById('subtitle-container');

    // 1. Показываем текст монолога
    subtitle.innerText = "Я ожидал всего, но не воровства от своих сотрудников. Ну что ж, буду разбираться во всем и искать человека, который решил выбиться из коллектива.";
    subtitle.style.display = 'block';

    // 2. ЗАПУСКАЕМ АУДИО "0 вводная"
    const introAudio = new Audio('./assets/videos/0.Вводная.mp3');
    introAudio.play().catch(e => console.warn("Аудио заблокировано браузером:", e));

    // 3. Ждем 4-5 секунд, пока проиграется фраза/прочитается текст
    await new Promise(r => setTimeout(r, 11000));

    // Далее стандартный переход
    kitchenSceneActive = false;
    await EngineUtils.showOverlay();

    // Очистка и запуск следующей сцены
    renderer.setAnimationLoop(null);
    EngineUtils.disposeObject(scene);
    scene.clear();
    orbitControls.dispose();
    composer.dispose();
    renderer.dispose();
    renderer.domElement.remove();
    labelRenderer.domElement.remove();

    if (MobileControls) MobileControls.hide();

    await initMainScene();

    setTimeout(() => {
        EngineUtils.hideOverlay();
        isAnimating = false;
        subtitle.style.display = 'none';
    }, 300);
}

function animateKitchen() {
    if (!kitchenSceneActive) return;
    if (orbitControls && !isAnimating) orbitControls.update();
    composer.render();
    labelRenderer.render(scene, camera);
}